import * as THREE from 'three';
import type { SceneBundle } from './scene/buildScene';

export type RenderQuality = 'low' | 'med' | 'high';

export interface RenderQualitySettings {
  shadowMapSize: number;
  antialias: boolean;
  fogEnabled: boolean;
}

export const RENDER_QUALITY = {
  low: { shadowMapSize: 512, antialias: false, fogEnabled: false },
  med: { shadowMapSize: 1024, antialias: true, fogEnabled: true },
  high: { shadowMapSize: 2048, antialias: true, fogEnabled: true },
} as const satisfies Record<RenderQuality, RenderQualitySettings>;

/**
 * Mutates render-time settings without reconstructing scene geometry. The
 * `antialias` value is recorded for the WebGLRenderer constructor used by the
 * app shell; Three.js cannot toggle context antialiasing after context creation.
 */
export const applyRenderQuality = (bundle: SceneBundle, quality: RenderQuality): void => {
  const settings = RENDER_QUALITY[quality];
  bundle.quality = quality;
  bundle.shadowMapSize = settings.shadowMapSize;
  bundle.antialias = settings.antialias;
  bundle.fogEnabled = settings.fogEnabled;
  bundle.scene.fog = settings.fogEnabled ? bundle.fogTemplate.clone() : null;

  bundle.scene.traverse((object) => {
    if (!(object instanceof THREE.DirectionalLight)) return;
    object.shadow.map?.dispose();
    object.shadow.map = null;
    object.shadow.mapSize.set(settings.shadowMapSize, settings.shadowMapSize);
  });
};
